import React, { useEffect, useState, useContext, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { ErrorButton, PrimaryButton } from "../../../../components/ui/Button";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { PaymentTermLoanContext } from "../../../../hooks/context";
import { InputField } from "../../../../components/ui/Inputs";
import { formatIndianRupees } from "../../../../utils/formatting";
import {
  encryptObject,
  decryptObject,
  decrypt,
} from "../../../../utils/crypto";
import postsServices from "../../../../services/api/service/postsService";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import ButtonLoader from "../../../../components/ui/ButtonLoader";
import { PositiveNumberValidation } from "../../../../utils/helpers";
import { formatNormalDate } from "../../../../utils/dateFormatUtils";
import Card from "../../../../components/ui/Card";

export interface RowData {
  [key: string]: string | number | null;
}

const OverdueLoanRequest = () => {
  const {
    errorMessage,
    setFormStep,
    dashboardData,
    overDueValues,
    formValue,
    userProfile,
    selectedLoanNumber,
    applicableCharges,
    setIsLoading,
    setTermLoanRequestId,
    handleEmptyFormValue,
    partPaymentData,
    loanCategoryValue,
    isXsOrSm
  }: any = useContext(PaymentTermLoanContext);

  const dispatch = useDispatch();
  const [amountValue, setAmountValue] = useState<number | null>(null);
  const [fieldError, setFieldError] = useState<string | null>("");
  const [isOverDueLoading, setIsOverDueLoading] = useState<boolean>(false);

  useEffect(() => {
      // Scroll to the top of the page when the component mounts
      window.scrollTo({
        top: 0,
        behavior: "smooth", // Adds smooth scrolling
      });
    }, []);

    
  /**
   * Validates the entered amount against the existing overdue amount.
   * If the entered amount is greater than 0 and less than the existing overdue amount,
   * it sets a field error indicating that the amount should be greater than the existing overdue amount.
   * Otherwise, it clears any field error.
   *
   * @param {any} event - The event object from the input field.
   */
  const handleAmountValidation = (event: any) => {
    const amount: number = event.target.value;
    const existingOverDueAmount =
      overDueValues?.bounceCharge +
      overDueValues?.penalCharge +
      overDueValues?.emiWithoutCharge;
    if (amount > existingOverDueAmount) {
      setFieldError(
        "Amount should not be greater than existing overdue amount"
      );
    } else {
      setFieldError("");
    }
    setAmountValue(amount);
  };

  const handleSubmit = async () => {
    setIsOverDueLoading(true);
    try {
      const copyFormValue = { ...formValue };
      copyFormValue["cif"] = decrypt(userProfile?.cifNumber);
      copyFormValue["cifEntity"] = decrypt(userProfile?.cifEntity);
      copyFormValue["lan"] = selectedLoanNumber;
      copyFormValue["initiatedBy"] = decrypt(userProfile?.id);
      copyFormValue["amount"] = amountValue;
      copyFormValue["paymentType"] = "OverDue";
      copyFormValue["loanCategory"] = loanCategoryValue?.loanCategoryId;
      copyFormValue["business"] = loanCategoryValue?.businessId;

      setIsLoading(true);
      const encryptedData = encryptObject(copyFormValue);
      await postsServices
        .addPost("/api/v1/payment/term-loan-data-processed", {
          data: encryptedData,
        })
        .then((response: any) => {
          setTermLoanRequestId(response?.data?.data?.termLoanRequestId);
          localStorage.setItem(
            "termLoanRequestId",
            response?.data?.data?.termLoanRequestId
          );
          setFormStep(3);
          setIsLoading(false);
          setAmountValue(null);
          handleEmptyFormValue();
          setIsOverDueLoading(false);
        })
        .catch((error: any) => {
          const errorMessage = error?.response?.data?.message;
          setFieldError(errorMessage);
          setIsLoading(false);
          setIsOverDueLoading(false);
        });
    } catch (error: any) {
      setIsLoading(false);
      setIsOverDueLoading(false);
      console.log(error);
    }
  };

  const showOverDueHeader = useMemo(() => {
    return (
      <Box className="dashboard-container mb-20">
        <Grid container item spacing={1}>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageLablePayment">
              Loan Account Number
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageValueItem">
              {dashboardData?.lanNumber}
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageLablePayment">
              Sanctioned Amount
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageValueItem">
              {formatIndianRupees(dashboardData?.repayAmount)}
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageLablePayment ">EMI Payment</Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageValueItem">
              {formatIndianRupees(partPaymentData?.[0]?.emiAmount)}
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageLablePayment">
              Next EMI Payment Date
            </Typography>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Typography className="insidePageValueItem">
              {formatNormalDate(partPaymentData?.[0]?.nextEmiDate)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    )
  }, [dashboardData])

  return (
    <>
      {isXsOrSm && (
        <>
          <Box mb={2}>
            <Card>
              <Box>
                <Typography className="section-sub-heading mt-10 mb-20">
                  LOAN DETAILS
                </Typography>
              </Box>
              {showOverDueHeader}
            </Card>
          </Box>
        </>
      )}

      <Card>
        <Grid item sm={12} className={`${isXsOrSm ? "dashboard-container-mobile" : ""} `}>
          <Box>
            <Typography className="section-sub-heading mt-10 mb-20">
              OVERDUE PAYMENT
            </Typography>
          </Box>

          {!isXsOrSm && (
            <>
              {showOverDueHeader}
            </>
          )}


          <Grid container item spacing={2} mb={1} sx={{ alignItems: "center" }}>
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <div style={{ marginBottom: "2px" }}>
                <label
                  htmlFor="custom-autocomplete"
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "rgba(75, 70, 92, 0.8)",
                    lineHeight: "18px",
                  }}
                >
                  Existing Overdue(Incl. charges)
                </label>
              </div>
              <InputField
                fullWidth
                placeholder="Enter Existing Overdue(Incl. charges)"
                type="number"
                autoComplete="off"
                value={
                  overDueValues?.bounceCharge +
                  overDueValues?.penalCharge +
                  overDueValues?.emiWithoutCharge
                }
                disabled
              // onChange={handleInputChange}
              // error={!!errorMessage}
              // helperText={errorMessage}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <div style={{ marginBottom: "2px" }}>
                <label
                  htmlFor="custom-autocomplete"
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "rgba(75, 70, 92, 0.8)",
                    lineHeight: "18px",
                  }}
                >
                  Overdue Amount
                </label>
              </div>
              <InputField
                fullWidth
                placeholder="Enter Overdue Amount"
                type="number"
                autoComplete="off"
                disabled
                value={overDueValues?.emiWithoutCharge}
              // onChange={handleInputChange}
              // error={!!errorMessage}
              // helperText={errorMessage}
              />
            </Grid>

            <Grid item xs={12} sm={3} md={3} lg={3}>
              <div style={{ marginBottom: "2px" }}>
                <label
                  htmlFor="custom-autocomplete"
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "rgba(75, 70, 92, 0.8)",
                    lineHeight: "18px",
                  }}
                >
                  Penal Charges
                </label>
              </div>
              <InputField
                fullWidth
                placeholder="Enter Penal Charges"
                type="number"
                autoComplete="off"
                disabled
                value={overDueValues?.penalCharge}
              // onChange={handleInputChange}
              // error={!!errorMessage}
              // helperText={errorMessage}
              />
            </Grid>

            <Grid item xs={12} sm={3} md={3} lg={3}>
              <div style={{ marginBottom: "2px" }}>
                <label
                  htmlFor="custom-autocomplete"
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "rgba(75, 70, 92, 0.8)",
                    lineHeight: "18px",
                  }}
                >
                  Bounce Charges
                </label>
              </div>
              <InputField
                fullWidth
                placeholder="Enter Bounce Charges"
                type="number"
                autoComplete="off"
                disabled
                value={overDueValues?.bounceCharge}
              // onChange={handleInputChange}
              // error={!!errorMessage}
              // helperText={errorMessage}
              />
            </Grid>

            <Grid item xs={12} sm={3} md={3} lg={3}>
              <div style={{ marginBottom: "2px" }}>
                <label
                  htmlFor="custom-autocomplete"
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "rgba(75, 70, 92, 0.8)",
                    lineHeight: "18px",
                  }}
                >
                  Amount to Pay
                </label>
              </div>
              <InputField
                fullWidth
                placeholder="Enter Amount to Pay"
                type="number"
                inputProps={{
                  onKeyPress: PositiveNumberValidation, // Restrict negative input
                  min: 0, // Optional, for numeric fields to set a minimum value
                }}
                autoComplete="off"
                onChange={handleAmountValidation}
                error={!!errorMessage}
                helperText={errorMessage}
              />
            </Grid>
          </Grid>

          <Typography sx={{ color: "#d32f2f", fontSize: "13px" }}>
            {fieldError}
          </Typography>

          <Grid
            container
            spacing={2} // Maintain spacing between items
            mt={1}
            mb={isXsOrSm ? 2 : 0}
          >
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              justifyContent="flex-end" // Align content to the right
            >
              <PrimaryButton
                width={"127px"}
                onClick={() => handleSubmit()}
                disabled={
                  isOverDueLoading ||
                  !!fieldError ||
                  !amountValue ||
                  amountValue <= 0
                }
              >
                <ButtonLoader title="Proceed" isLoading={isOverDueLoading} />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default OverdueLoanRequest;